import React, { useEffect, useRef, lazy } from 'react';
import { motion } from 'framer-motion';
import SmoothScroll from './components/SmoothScroll';
import AmbientBackdrop from './components/AmbientBackdrop';
import LazySection from './components/LazySection';
import PageTransition from './components/animations/PageTransition';
import Nav from './components/Nav';
import Banner from './components/Banner';
import Seo from './components/Seo';
import { fadeInUp, viewportSection } from './lib/motion';
import { IS_PRERENDER } from './lib/prerender';
import { featured, projects, services, experiences, articles } from './requests';

const Row = lazy(() => import('./components/Row'));
const ServicesRow = lazy(() => import('./components/ServiceRow'));
const Timeline = lazy(() => import('./components/Timeline'));
const ArticlesRow = lazy(() => import('./components/ArticlesRow'));
const Faq = lazy(() => import('./components/Faq'));
const Footer = lazy(() => import('./components/Footer'));

function App() {
    const gradientRef = useRef(null);

    const sectionMotion = IS_PRERENDER
        ? {}
        : {
            initial: 'initial',
            whileInView: 'animate',
            viewport: viewportSection,
            variants: fadeInUp
        };

    useEffect(() => {
        let raf = null;

        const handleMouseMove = (e) => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = null;
                if (gradientRef.current) {
                    gradientRef.current.style.background =
                        `radial-gradient(600px circle at ${e.clientX}px ${e.clientY}px, rgba(99, 102, 241, 0.12), transparent 80%)`;
                }
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    return (
        <SmoothScroll>
            <div className="relative min-h-screen bg-[#090D16] text-slate-100 font-sans selection:bg-indigo-500/30 selection:text-indigo-200 overflow-hidden">

                {/* SEO: document title + structured data */}
                <Seo />

                {/* Initial page load slide sweep */}
                <PageTransition />

                {/* Dynamic Mouse-Following Soft Radial Background Gradient (rAF-throttled, no re-renders) */}
                <div
                    ref={gradientRef}
                    className="pointer-events-none fixed -inset-px z-30 opacity-40 md:opacity-60"
                    style={{
                        background: 'radial-gradient(600px circle at 50% 20%, rgba(99, 102, 241, 0.12), transparent 80%)'
                    }}
                />

                {/* Floating ambient gradient mesh that shifts subtly with the cursor */}
                <AmbientBackdrop />

                {/* Sticky Navigation Header */}
                <Nav />

                {/* Main Content Sections */}
                <main className="relative z-10">
                    <section id="home" aria-label="Introduction">
                        <Banner />
                    </section>

                    <motion.section
                        id="projects"
                        aria-label="Projects"
                        className="relative"
                        {...sectionMotion}
                    >
                        <LazySection minHeight="min-h-[520px]">
                            <Row
                                title="Featured & Key Projects"
                                subtitle="A showcase of fullstack applications, computer vision systems, and AI utilities."
                                items={featured}
                            />
                        </LazySection>
                        <LazySection minHeight="min-h-[760px]">
                            <Row
                                title="All Creative Projects"
                                subtitle="Modular Python implementations, mobile tools, and client web applications."
                                items={projects}
                            />
                        </LazySection>
                    </motion.section>

                    <motion.section
                        id="skills"
                        aria-label="Skills"
                        className="relative"
                        {...sectionMotion}
                    >
                        <LazySection minHeight="min-h-[700px]">
                            <section id="services" className="relative">
                                <ServicesRow title="Core Services & Consulting" items={services} />
                            </section>
                        </LazySection>
                    </motion.section>

                    <motion.section
                        id="about"
                        aria-label="Professional Experience"
                        className="relative"
                        {...sectionMotion}
                    >
                        <LazySection minHeight="min-h-[620px]">
                            <Timeline title="Professional Experience" items={experiences} />
                        </LazySection>
                    </motion.section>

                    <motion.section
                        id="articles"
                        aria-label="Articles and Insights"
                        className="relative"
                        {...sectionMotion}
                    >
                        <LazySection minHeight="min-h-[460px]">
                            <ArticlesRow title="Latest Articles & Insights" items={articles} />
                        </LazySection>
                    </motion.section>

                    <motion.section
                        id="faq"
                        aria-label="Frequently Asked Questions"
                        className="relative"
                        {...sectionMotion}
                    >
                        <LazySection minHeight="min-h-[520px]">
                            <Faq />
                        </LazySection>
                    </motion.section>
                </main>

                {/* Footer & Contact Section */}
                <section id="contact" aria-label="Contact" className="relative z-10">
                    <LazySection minHeight="min-h-[300px]">
                        <Footer />
                    </LazySection>
                </section>
            </div>
        </SmoothScroll>
    );
}

export default App;